// /settings/billing (hosted-only, docs/09 §4). Renders the org's credit
// billing projection from GET /v1/billing/entitlement: active plan, billing
// state, credit balance vs monthly allotment, usage banners, and the
// usage-based pricing readout. The "Manage subscription" action opens the
// Stripe Customer Portal (POST /v1/billing/portal:create-session) with a
// full-page redirect. Self-managed installs report `credit_billing_enabled:
// false` on GET /v1/me, so the screen redirects home — it does not exist there.
import { useState } from "react";

import { Navigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../lib/apiClient";
import {
  creditUsageState,
  formatCredits,
  getBillingEntitlement,
  humanizePlanKey,
  updateUsageBasedPricing,
  useCreditBilling,
  usageSpendCapBounds,
  type BillingEntitlement,
  type UsageBasedPricingUpdate,
} from "../lib/billing";
import {
  ApiRequestError,
  isApiRequestError,
  requestErrorDescription,
} from "../lib/errors";
import { docUrl } from "../lib/docLinks";
import { queryKeys } from "../lib/queryKeys";
import {
  Badge,
  Button,
  HelpTip,
  Inline,
  InlineError,
  KeyValue,
  Page,
  Progress,
  Section,
  Select,
  Skeleton,
  Stack,
  StatCard,
  Text,
  TextInput,
  useToast,
  type BadgeProps,
} from "../ui";

// `billing_state` is a free-form projection string (docs/04); the tone/label
// here is a display affordance only — an unknown value renders neutrally with
// its humanized text. ASSUMED(billing_state tone mapping — display only).
function billingStateTone(state: string): NonNullable<BadgeProps["tone"]> {
  if (state === "active") {
    return "success";
  }
  if (state === "past_due" || state === "canceled" || state === "expired") {
    return "danger";
  }
  return "warning";
}

function humanizeState(state: string): string {
  const words = state.replace(/[_-]+/g, " ").trim();
  return words.length > 0
    ? words.charAt(0).toUpperCase() + words.slice(1)
    : state;
}

// The two credit-usage banners (>=80% info, >=100% stronger warning). Only
// meaningful with a positive allotment.
function UsageBanner({
  balance,
  allotment,
}: {
  balance: number;
  allotment: number;
}) {
  const state = creditUsageState(balance, allotment);
  if (state === "exhausted") {
    return (
      <Inline align="baseline" gap={3} role="alert">
        <Badge tone="danger">Limit reached</Badge>
        <Text>You&apos;ve used your full monthly credit allotment.</Text>
      </Inline>
    );
  }
  if (state === "warn_80") {
    return (
      <Inline align="baseline" gap={3} role="status">
        <Badge tone="warning">Almost out</Badge>
        <Text>You&apos;ve used 80% of your monthly credits.</Text>
      </Inline>
    );
  }
  return null;
}

export function BillingSettings() {
  const creditBilling = useCreditBilling();
  const toast = useToast();

  const entitlement = useQuery({
    enabled: creditBilling.enabled,
    queryKey: queryKeys.billingEntitlement,
    queryFn: getBillingEntitlement,
  });

  const openPortal = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        "/v1/billing/portal:create-session",
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: (data) => {
      // Full-page redirect to the external Stripe Customer Portal.
      window.location.assign(data.portal_url);
    },
    onError: (error) => {
      toast.error("Could not open the customer portal", {
        description: portalErrorMessage(error),
      });
    },
  });

  // Hosted gate: while /v1/me resolves, hold; a self-managed install redirects
  // home (the screen does not exist there).
  if (creditBilling.isPending) {
    return (
      <Page variant="config">
        <Skeleton label="Loading billing" rows={4} shape="detail-field" />
      </Page>
    );
  }
  if (!creditBilling.enabled) {
    return <Navigate to="/" />;
  }

  return (
    <Page variant="config">
      {entitlement.isPending ? (
        <Skeleton label="Loading billing" rows={4} shape="detail-field" />
      ) : entitlement.isError ? (
        <InlineError>{entitlement.error.message}</InlineError>
      ) : (
        <BillingSettingsView
          entitlement={entitlement.data}
          isPortalPending={openPortal.isPending}
          onOpenPortal={() => {
            openPortal.mutate();
          }}
          portalError={
            openPortal.isError ? portalErrorMessage(openPortal.error) : null
          }
        />
      )}
    </Page>
  );
}

function portalErrorMessage(error: unknown): string {
  if (isApiRequestError(error) && error.status.code === "CONFLICT") {
    return "The customer portal is available after your first checkout.";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Request failed.";
}

interface BillingSettingsViewProps {
  entitlement: BillingEntitlement;
  isPortalPending: boolean;
  onOpenPortal: () => void;
  portalError: string | null;
}

export function BillingSettingsView({
  entitlement,
  isPortalPending,
  onOpenPortal,
  portalError,
}: BillingSettingsViewProps) {
  const {
    billing_state,
    credit_allotment,
    credit_balance,
    outstanding_credit_debt,
    plan_key,
  } = entitlement;

  // Narrowed to a positive number, else null — usage meters/banners only apply
  // once a monthly allotment settles.
  const allotment =
    credit_allotment != null && credit_allotment > 0 ? credit_allotment : null;

  // Debt can drive credit_balance negative; spendable credits clamp to zero
  // (matching the sidebar and Usage meters) while the debt card shows the
  // negative side explicitly.
  const spendableCredits = Math.max(credit_balance, 0);

  return (
    <Stack gap={6}>
      <Section heading="Plan">
        <Stack gap={4}>
          <KeyValue
            rows={[
              {
                label: "Plan",
                value: plan_key ? humanizePlanKey(plan_key) : "No active plan",
              },
              {
                label: "Status",
                value: (
                  <Badge tone={billingStateTone(billing_state)}>
                    {humanizeState(billing_state)}
                  </Badge>
                ),
              },
            ]}
          />
        </Stack>
      </Section>

      <Section heading="Credits">
        <Stack gap={4}>
          {allotment != null ? (
            <UsageBanner allotment={allotment} balance={spendableCredits} />
          ) : null}
          <StatCard
            label="Credit balance"
            value={`${formatCredits(spendableCredits)} credits`}
          />
          {outstanding_credit_debt > 0 ? (
            <StatCard
              label="Outstanding credit debt"
              value={`${formatCredits(outstanding_credit_debt)} credits`}
            />
          ) : null}
          {allotment != null ? (
            <Stack gap={2}>
              <Progress
                label="Credit balance versus monthly allotment"
                max={allotment}
                value={spendableCredits}
              />
              <Text variant="muted">
                {formatCredits(spendableCredits)} of {formatCredits(allotment)}{" "}
                monthly credits remaining.
              </Text>
            </Stack>
          ) : (
            <Text variant="muted">
              Appears once your first invoice settles.
            </Text>
          )}
        </Stack>
      </Section>

      <UsageBasedPricingSection entitlement={entitlement} />

      <Section heading="Subscription">
        <Stack gap={3}>
          {portalError ? <InlineError>{portalError}</InlineError> : null}
          <Inline>
            <Button
              disabled={isPortalPending}
              type="button"
              onClick={onOpenPortal}
            >
              {isPortalPending ? "Opening..." : "Manage subscription"}
            </Button>
          </Inline>
        </Stack>
      </Section>
    </Stack>
  );
}

// Live owner-gated usage-based-pricing control (PATCH /v1/billing/usage-based).
// The toggle opts the org in/out of overage pricing; enabling requires a hard
// spend cap between 1x and 2x the monthly allotment. The same range is
// validated client-side here and authoritatively re-checked by core.
export function UsageBasedPricingSection({
  entitlement,
}: {
  entitlement: BillingEntitlement;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const bounds = usageSpendCapBounds(entitlement.credit_allotment);

  const [enabled, setEnabled] = useState(
    entitlement.usage_based_pricing_enabled,
  );
  const [capInput, setCapInput] = useState(
    entitlement.usage_spend_cap_credits != null
      ? String(entitlement.usage_spend_cap_credits)
      : bounds != null
        ? String(bounds.min)
        : "",
  );

  const save = useMutation({
    mutationFn: (body: UsageBasedPricingUpdate) =>
      updateUsageBasedPricing(body),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.billingEntitlement, data);
      toast.success("Usage-based pricing updated");
    },
    onError: (error) => {
      toast.error("Could not update usage-based pricing", {
        description: requestErrorDescription(error),
      });
    },
  });

  // Usage-based pricing overages are metered against the monthly allotment, so
  // it cannot be enabled until a plan is active and the allotment has settled.
  if (bounds == null) {
    return (
      <Section heading="Usage-based pricing">
        <Text variant="muted">
          Available once your plan is active and your allotment settles.
        </Text>
      </Section>
    );
  }

  const parsedCap = Number(capInput);
  const capValid =
    Number.isInteger(parsedCap) &&
    parsedCap >= bounds.min &&
    parsedCap <= bounds.max;
  const capError =
    enabled && capInput.trim() !== "" && !capValid
      ? `Enter a whole number between ${formatCredits(bounds.min)} and ` +
        `${formatCredits(bounds.max)} credits.`
      : null;
  const canSave = !save.isPending && (!enabled || capValid);

  return (
    <Section
      description="Off stops new work at your allotment."
      heading="Usage-based pricing"
      help={
        <HelpTip
          docHref={docUrl("account/billing")}
          docLabel="Billing"
          label="About usage-based pricing"
        >
          {"On reserves metered overage above your allotment, up to a spend " +
            "cap you set between 1x and 2x the allotment."}
        </HelpTip>
      }
    >
      <Stack gap={4}>
        <Select
          label="Usage-based pricing"
          value={enabled ? "on" : "off"}
          onChange={(event) => {
            setEnabled(event.currentTarget.value === "on");
          }}
        >
          <option value="off">Off: hard stop at your monthly allotment</option>
          <option value="on">On: allow overage up to a spend cap</option>
        </Select>
        {enabled ? (
          <TextInput
            description={
              `${formatCredits(bounds.min)} to ` +
              `${formatCredits(bounds.max)} credits.`
            }
            error={capError}
            inputMode="numeric"
            label="Monthly spend cap (credits)"
            max={bounds.max}
            min={bounds.min}
            step={1}
            type="number"
            value={capInput}
            onChange={(event) => {
              setCapInput(event.currentTarget.value);
            }}
          />
        ) : null}
        <Inline>
          <Button
            disabled={!canSave}
            type="button"
            onClick={() => {
              save.mutate({
                usage_based_pricing_enabled: enabled,
                // The DTO always carries a cap; the range is enforced only on
                // enable. When disabling, send the entered (or floor) value.
                usage_spend_cap_credits: Number.isInteger(parsedCap)
                  ? parsedCap
                  : bounds.min,
              });
            }}
          >
            {save.isPending ? "Saving..." : "Save"}
          </Button>
        </Inline>
      </Stack>
    </Section>
  );
}
